/**
 * Timeline Focus View
 *
 * Two-column triage view for quickly processing attention items.
 * Left column: "Action needed" — issue cards that require attention.
 * Right column: "Requires review" — non-issue cards that require attention and haven't been read.
 */

import { useMemo } from 'react';
import { AlertCircle, CheckCircle, Eye } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import type { BackendActivity } from '@/data/api/activities';
import { ExpandableActivityCard } from './expandable-activity-card';

interface TimelineFocusViewProps {
    activities: BackendActivity[];
    containerId: string;
}

export function TimelineFocusView({ activities, containerId }: TimelineFocusViewProps) {
    const { actionNeeded, requiresReview } = useMemo(() => {
        const actionNeeded: BackendActivity[] = [];
        const requiresReview: BackendActivity[] = [];

        for (const activity of activities) {
            if (activity.entity_type === 'container_issue' && activity.requires_attention) {
                actionNeeded.push(activity);
            } else if (activity.entity_type !== 'container_issue' && activity.requires_attention && !activity.is_read) {
                requiresReview.push(activity);
            }
        }

        return { actionNeeded, requiresReview };
    }, [activities]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Left: Action needed (issues) */}
            <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="h-4 w-4 text-warning-600" />
                    <h3 className="text-sm font-semibold text-primary">Action needed</h3>
                    {actionNeeded.length > 0 && (
                        <Badge color="warning" size="sm">{actionNeeded.length}</Badge>
                    )}
                </div>
                {actionNeeded.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-secondary py-10">
                        <CheckCircle className="h-8 w-8 text-success-400 mb-2" />
                        <p className="text-sm text-tertiary">No actions needed</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {actionNeeded.map((activity) => (
                            <div
                                key={activity.activity_id}
                                className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary"
                            >
                                <ExpandableActivityCard
                                    activity={activity}
                                    containerId={containerId}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Right: Requires review (non-issues) */}
            <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                    <Eye className="h-4 w-4 text-brand-600" />
                    <h3 className="text-sm font-semibold text-primary">Requires review</h3>
                    {requiresReview.length > 0 && (
                        <Badge color="blue" size="sm">{requiresReview.length}</Badge>
                    )}
                </div>
                {requiresReview.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-secondary py-10">
                        <CheckCircle className="h-8 w-8 text-success-400 mb-2" />
                        <p className="text-sm text-tertiary">All caught up</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {requiresReview.map((activity) => (
                            <div
                                key={activity.activity_id}
                                className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary"
                            >
                                <ExpandableActivityCard
                                    activity={activity}
                                    containerId={containerId}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
